import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import TreeNode from "./TreeNode.jsx";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card.jsx";
import { Checkbox } from "./ui/checkbox.jsx";
import { ScrollArea } from "./ui/scroll-area.jsx";
import { Badge } from "./ui/badge.jsx";
import { X, Info, ChevronRight } from "lucide-react";
import { Label } from "./ui/label.jsx";

import { getUniqueValues } from "../../actions/uniqueValues.js";
import styles from "./Styles/InventoryManagement.module.css";
import SaveTemplateModal from "./SaveTemplateModal.jsx";
import { toast } from "react-toastify";

import { saveTemplate } from "../../actions/saveTemplate.js";
import { useDispatch, useSelector } from "react-redux";

import { Breadcrumb } from "./ui/breadcrumb.jsx";
import { AppSidebar } from "./ui/sidebar";
import debounce from "lodash.debounce";
import { getInventoryDetail } from "../../actions/inventoryDetail.js";
import { getListOfColumns } from "../../actions/inventoryBuilder.js";
import {
  toggleFavorite,
  fetchFavoriteInventory,
} from "../../actions/toggleFavorite.js";
import { useNavigate } from "react-router-dom";
import Header from "../header";
import { decryptData } from "../utils/EncriptionStore.js";

const InventoryDetail = () => {
  const user = (() => {
    const encryptedData = localStorage.getItem("profile");
    return encryptedData ? decryptData(encryptedData) : null;
  })();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const toggleSidebar = () => setIsOpen((prev) => !prev);

  const searchParams = new URLSearchParams(window.location.search);

  const [inventory_name, setInventoryName] = useState();

  useEffect(() => {
    const invName = searchParams.get("inventory_name");
    if (invName) {
      setInventoryName(invName);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(
      getInventoryDetail({
        inv_name: inventory_name,
        customer_id: user?.customer_id,
        group_id: user?.group_id,
        customer_name: user?.customer_name,
        group_name: user?.groupname,
      })
    )
      .then((resultAction) => {
        if (getInventoryDetail.fulfilled.match(resultAction)) {
        } else {
          console.error(
            "Failed to fetch inventory details:",

            resultAction.error
          );
        }
      })

      .catch((error) => {
        console.error("Error fetching inventory details:", error);
      });
  }, [inventory_name]);

  const inventoryDetail = useSelector(
    (state) => state.inventoryDetail.inventoryDetail
  );

  const schema =
    Object.keys(inventoryDetail).length > 0 &&
    inventoryDetail?.inventory_schema;

  const table_name =
    Object.keys(inventoryDetail).length > 0 &&
    inventoryDetail?.inventory_source_table;
  useEffect(() => {
    if (inventoryDetail) {
      const schema =
        Object.keys(inventoryDetail).length > 0 &&
        inventoryDetail?.inventory_schema;

      const table_name =
        Object.keys(inventoryDetail).length > 0 &&
        inventoryDetail?.inventory_source_table;

      const loadColumns = async () => {
        setLoading(true);

        try {
          const columns = await dispatch(
            getListOfColumns({ schema, table_name })
          );

          const selectedSet = new Set(columns);
          const selectedColumns = columnsFromStore
            .filter((col) => selectedSet.has(col))
            .map((name) => ({ name, selected: true }));

          const selectedNames = new Set(columnsFromStore);
          const remainingColumns = columns
            .filter((name) => !selectedNames.has(name))
            .map((name) => ({ name, selected: false }));

          setAvailableColumns([...selectedColumns, ...remainingColumns]);
        } catch (error) {
          console.error("Error loading columns:", error);
          setAvailableColumns([]);
        } finally {
          setLoading(false);
        }
      };

      if (schema && table_name) {
        loadColumns();
      }
    }
  }, [inventoryDetail]);

  const columnDataFromStore =
    inventoryDetail &&
    Object.keys(inventoryDetail).length > 0 &&
    inventoryDetail?.inventory_column;

  const columnsFromStore = useMemo(() => {
    return columnDataFromStore
      ? columnDataFromStore.split(",").map((col) => col.trim())
      : [];
  }, [columnDataFromStore]);

  const [treeData, setTreeData] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  // Update availableColumns based on store
  useEffect(() => {
    if (columnsFromStore.length > 0) {
      setLoading(true);
      setAvailableColumns(
        columnsFromStore.map((name) => ({
          name,

          selected: true,
        }))
      );
      setLoading(false);
    } else {
      setLoading(true);

      setAvailableColumns([]);
      setLoading(false);
    }
  }, [columnsFromStore]);

  const showApplyToast = useCallback(
    debounce(() => {
      toast.info("Please click 'Apply Columns' to update the pivot tree.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        style: {
          background: "#ffffff",
          color: "green",
          fontWeight: "bold",
        },
      });
    }, 500),
    []
  );

  const handleColumnToggle = (column) => {
    setAvailableColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.name === column.name
          ? {
              ...col,
              selected: !col.selected,
              selectedAt: !col.selected ? Date.now() : undefined,
            }
          : col
      )
    );
    showApplyToast();
  };

  const hasAppliedColumns = useRef(false);

  const applyColumns = async () => {
    dispatch(fetchFavoriteInventory());
    const selected = availableColumns

      .filter((col) => col.selected)
      .sort((a, b) => a.selectedAt - b.selectedAt)
      .map((col) => col.name);

    if (selected.length === 0) {
      toast.error(
        "Please select at least one column to create the pivot tree."
      );
      setTreeData([]);
      setSelectedColumns([]);
      return;
    }
    setSelectedColumns(selected);
    setTreeData([]);
    const rootColumn = selected[0];
    const params = {
      column: rootColumn,
      schema: schema,
      table: table_name,
      conditions: {},
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };
    setLoading(true);
    try {
      const resultAction = await dispatch(getUniqueValues(params));
      const values = resultAction;
      const rootNodes = Array.isArray(values)
        ? values.map((value) => ({
            id: `${rootColumn}-${value}`,
            label: value,
            level: 0,
            expanded: false,
            column: rootColumn,
            value: value,
            children: selected.length > 1 ? [] : undefined,
            isLeaf: selected.length === 1,
          }))
        : [];
      setTreeData(rootNodes);
    } catch (error) {
      setTreeData([]);
      toast.error("Could not fetch data from API. Using sample data instead.");
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const allSelected =
  //     availableColumns.length > 0 &&
  //     availableColumns.every((col) => col.selected);

  //   if (allSelected && !hasAppliedColumns.current) {
  //     hasAppliedColumns.current = true;
  //     applyColumns();
  //   }
  // }, [availableColumns]);

  const isSaveDisabled =
    loading || !Array.isArray(selectedColumns) || selectedColumns.length === 0;

  const handleSaveTemplate = async (template) => {
    const favoriteLabels = collectFavoriteLabels(treeData);
    const uniqueFavoriteLabels = [...new Set(favoriteLabels)];

    const inventoryKey =
      inventory_name && inventory_name.trim() !== ""
        ? inventory_name
        : template?.name || "";

    const payload = {
      [inventoryKey]: uniqueFavoriteLabels,
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const result = await dispatch(saveTemplate(template, navigate));

      // ✅ Only call favorite API if there are favorites
      if (favoriteLabels.length > 0) {
        try {
          const favoriteResult = await dispatch(toggleFavorite(payload));
          toast.success(
            favoriteResult?.content || "Favorites saved successfully!"
          );
        } catch (favoriteError) {
          toast.error(
            favoriteError?.message || "Failed to save favorites after template."
          );
        }
      }
    } catch (error) {
      toast.info(error || "Failed to save template.");
    }
  };

  const toggleNode = (nodeId) => {
    const toggleExpanded = (nodes) => {
      let changed = false;

      const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          const expanded = !node.expanded;

          if (expanded && node.children && node.children.length === 0) {
            handleNodeExpand(nodeId, node);
          }

          changed = true;
          return { ...node, expanded };
        }

        if (node.children) {
          const updatedChildren = toggleExpanded(node.children);
          // Only update if children changed
          if (updatedChildren !== node.children) {
            changed = true;
            return { ...node, children: updatedChildren };
          }
        }

        return node;
      });

      return changed ? updatedNodes : nodes;
    };

    setTreeData(toggleExpanded(treeData));
  };

  // Handle node selection
  const selectNode = (nodes, selectedId = null) => {
    return nodes.map((node) => {
      if (node.children) {
        return {
          ...node,

          selected: node.id === selectedId,

          children: selectNode(node.children, selectedId),
        };
      }

      return {
        ...node,

        selected: node.id === selectedId,
      };
    });
  };

  const handleSelectNode = useCallback((nodeId) => {
    setTreeData((prevTreeData) => {
      const newTreeData = selectNode(prevTreeData, nodeId);
      return newTreeData !== prevTreeData ? newTreeData : prevTreeData;
    });
  }, []);

  const collectConditions = (node, tree) => {
    if (!node) {
      return {};
    }

    const conditions = {};

    if (node.column && node.value) {
      conditions[node.column] = String(node.value);
    }

    const parent = findParentNode(tree, node.id);

    if (parent) {
      const parentConditions = collectConditions(parent, tree);

      return { ...parentConditions, ...conditions };
    }

    return conditions;
  };

  const findParentNode = (nodes, nodeId) => {
    for (const node of nodes) {
      if (node.children && node.children.some((child) => child.id === nodeId)) {
        return node;
      }

      if (node.children) {
        const foundInChildren = findParentNode(node.children, nodeId);

        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }

    return null;
  };

  const handleNodeExpand = async (nodeId, node) => {
    if (node.children?.length > 0 || !node.column) return;

    const nodeIndex = selectedColumns.indexOf(node.column);
    if (nodeIndex === -1 || nodeIndex >= selectedColumns.length - 1) return;

    const childColumn = selectedColumns[nodeIndex + 1];
    const conditions = collectConditions(node, treeData);

    const params = {
      column: childColumn,
      schema,
      table: table_name,
      ...(Object.keys(conditions).length && { conditions }),
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const values = await dispatch(getUniqueValues(params)); // ✅ simplified

      if (!Array.isArray(values)) throw new Error("Invalid response");

      const isLastColumn = nodeIndex + 1 === selectedColumns.length - 1;
      const childNodes = values.map((value) => ({
        id: `${childColumn}-${value}-${nodeId}`,
        label: value,
        level: node.level + 1,
        expanded: false,
        column: childColumn,
        value,
        children: !isLastColumn ? [] : undefined,
        isLeaf: isLastColumn,
      }));

      setTreeData((prevTree) =>
        updateTreeNodeChildren(prevTree, nodeId, childNodes)
      );
    } catch (error) {
      console.error("Failed to load child values:", error);
      toast.error("Failed to load child values. Please try again.");
    }
  };

  const updateTreeNodeChildren = (tree, nodeId, childNodes) => {
    return tree.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,

          children: childNodes,
        };
      } else if (node.children) {
        return {
          ...node,

          children: updateTreeNodeChildren(node.children, nodeId, childNodes),
        };
      } else {
        return node;
      }
    });
  };

  // Helper function to find a node by ID in the tree
  const findNodeById = (nodes, nodeId) => {
    for (const node of nodes) {
      if (node.id === nodeId) {
        return node;
      }

      if (node.children) {
        const found = findNodeById(node.children, nodeId);

        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const handleFavoriteToggle = (targetNode) => {
    const toggleAllChildren = (node, newFavoriteState) => ({
      ...node,
      isFavorite: newFavoriteState,
      children: node.children
        ? node.children.map((child) =>
            toggleAllChildren(child, newFavoriteState)
          )
        : node.children,
    });

    const toggleFavorites = (nodes) => {
      return nodes.map((node) => {
        if (node.id === targetNode.id) {
          const newFavoriteState = !node.isFavorite;
          return toggleAllChildren(node, newFavoriteState);
        }
        if (node.children) {
          return {
            ...node,
            children: toggleFavorites(node.children),
          };
        }
        return node;
      });
    };

    setTreeData((prevTree) => toggleFavorites(prevTree));
  };

  const collectFavoriteLabels = (nodes) => {
    return nodes.flatMap((node) => {
      const favorites = [];
      if (node.isFavorite && node.isLeaf) {
        favorites.push(String(node.label));
      }
      if (node.children) {
        favorites.push(...collectFavoriteLabels(node.children));
      }
      return favorites;
    });
  };

  const TOAST_OPTIONS = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
  };

  const saveAsFavorite = async () => {
    const trimmedName = inventory_name?.trim();
    const inventoryKey = trimmedName || "";

    if (!inventoryKey) {
      toast.info(
        "Please save the inventory first before saving favorites.",
        TOAST_OPTIONS
      );
      return;
    }

    console.log(treeData, "Tree Data for Favorites:");

    // Efficient tree data handling assumed from your experience[1][2]
    const favoriteLabels = collectFavoriteLabels(treeData);
    const uniqueFavoriteLabels = [...new Set(favoriteLabels)];

    console.log("Collected Favorite Labels:", favoriteLabels);

    const payload = {
      [inventoryKey]: uniqueFavoriteLabels,
      customer_id: user?.customer_id,
      group_id: user?.group_id,
      customer_name: user?.customer_name,
      group_name: user?.groupname,
    };

    try {
      const resultAction = await dispatch(toggleFavorite(payload, navigate));
      toast.success(
        resultAction?.content || "Favorites saved successfully!",
        TOAST_OPTIONS
      );
    } catch (error) {
      toast.error(error?.message || "Failed to save favorites.", TOAST_OPTIONS);
    }
  };

  const listOfFavorites = useSelector(
    (state) => state.toggleFavorite.favorites
  );

  console.log("List of Favorites:", listOfFavorites);

  useEffect(() => {
    const returnNodeLabels = (node) => {
      let listOfNodeLabels = [];
      if (node) {
        listOfNodeLabels.push(node.label);
        if (node.children && node.children.length > 0) {
          node.children.forEach((child) => {
            listOfNodeLabels.push(...returnNodeLabels(child));
          });
        }
      }
      return listOfNodeLabels;
    };

    let allLabels = [];
    if (Array.isArray(treeData)) {
      treeData.forEach((node) => {
        allLabels.push(...returnNodeLabels(node));
      });
    }
  }, [treeData]);

  return (
    <div className={styles.inventoryManagementContainer}>
      <div className={styles.twoSidebars}>
        <Header />
        <div
          className={` ${isOpen ? styles.sidebarOpen : styles.sidebarClosed}`}
        >
          <AppSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
        </div>
      </div>

      <div className={`${styles.dashboardLayout}`}>
        <div className={styles.inventoryContainer}>
          <div className={styles.inventoryGrid}>
            <Card className={styles.inventoryCard}>
              <CardHeader className={styles.cardHeader}>
                <CardTitle className={styles.cardTitle}>
                  Select Columns
                </CardTitle>

                <CardDescription className={styles.cardDescription}>
                  Choose the columns to create your pivot hierarchy
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className={styles.inventoryContent}>
                  <div className={styles.inventorySubContent}>
                    {Array.isArray(availableColumns) &&
                    availableColumns.length > 0 ? (
                      <ScrollArea
                        className={`${styles.scrollAreaHeight} ${styles.scrollAreaPaddingX} ${styles.scrollAreaPaddingY}`}
                      >
                        <div className={styles.availableColumnsContainer}>
                          {availableColumns
                            .slice() // shallow copy
                            .sort((a, b) => {
                              if (a.selected && b.selected) {
                                return a.selectedAt - b.selectedAt; // both selected, sort by time
                              }
                              if (a.selected && !b.selected) return -1; // selected first
                              if (!a.selected && b.selected) return 1;
                              return a.name.localeCompare(b.name); // fallback: alphabetical
                            })
                            .map((column) => (
                              <div
                                key={column.name}
                                className={styles.inventoryColumnItemContainer}
                              >
                                <Checkbox
                                  id={`column-${column.name}`}
                                  checked={column.selected}
                                  onCheckedChange={() =>
                                    handleColumnToggle(column)
                                  }
                                />

                                <Label
                                  htmlFor={`column-${column.name}`}
                                  className={`${styles.inventoryTextSmall} ${styles.inventoryFontMedium} ${styles.inventoryLeadingNone} ${styles.inventoryPeerDisabled}`}
                                >
                                  {column.name}
                                </Label>
                              </div>
                            ))}
                        </div>
                      </ScrollArea>
                    ) : (
                      <div className={styles.emptyStateContainer}>
                        <Info className={styles.emptyStateIcon} />
                        <div className={styles.inventoryTextCenter}>
                          <h3 className={styles.emptyStateTitle}>
                            No Columns To Display
                          </h3>
                          <p className={styles.emptyStateText}>
                            Select schema and table to load the columns.
                          </p>
                        </div>
                        {selectedColumns.length > 0 && (
                          <button
                            onClick={applyColumns}
                            className={`${styles.commonButton} ${styles.outlineButton} ${styles.refreshButton}`}
                          >
                            Refresh Data
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className={styles.inventoryTextMutedForeground}>
                      Selected columns (order determines hierarchy):
                    </Label>

                    <div className={styles.badgeContainer}>
                      {availableColumns

                        .filter((col) => col.selected)

                        .sort((a, b) => {
                          if (a.selected && b.selected) {
                            return a.selectedAt - b.selectedAt; // selected: sort by time selected
                          }
                          if (a.selected && !b.selected) return -1; // selected first
                          if (!a.selected && b.selected) return 1;
                          return a.name.localeCompare(b.name); // both unselected: sort alphabetically
                        })

                        .map((column, index) => (
                          <Badge
                            key={column.name}
                            variant="secondary"
                            className={`${styles.badge} ${styles.inventoryColumnItemContainer}`}
                          >
                            <span>{index + 1}</span> <p>{column.name}</p>
                            <button
                              className={`${styles.commonButton} ${styles.inventoryMarginTop} ${styles.badgeButton}`}
                              onClick={() => handleColumnToggle(column)}
                            >
                              <X className={styles.inventorySizeTen} />
                            </button>
                          </Badge>
                        ))}
                    </div>
                  </div>

                  <div className={styles.inventoryButtonContainer}>
                    <button
                      onClick={applyColumns}
                      disabled={loading}
                      className={`${styles.commonButton} ${styles.applyButton}`}
                    >
                      {loading ? "Loading..." : "Apply Columns"}
                    </button>

                    <SaveTemplateModal
                      columns={availableColumns}
                      source_table={table_name}
                      schema={schema}
                      onSave={handleSaveTemplate}
                      trigger={
                        <button
                          disabled={isSaveDisabled}
                          className={`${styles.commonButton} ${styles.applyButton}`}
                        >
                          {loading ? "Loading..." : "Save Template"}
                        </button>
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={styles.inventoryCard}>
              <CardHeader className={styles.cardHeader}>
                <CardTitle className={styles.cardTitle}>Pivot Tree</CardTitle>

                <CardDescription className={styles.cardDescription}>
                  Explore your data in a hierarchical tree
                </CardDescription>
              </CardHeader>

              <CardContent>
                {treeData.length > 0 ? (
                  <div className={styles.inventoryContent}>
                    <div className={styles.inventorySubContent}>
                      <ScrollArea className={styles.scrollAreaHeight}>
                        <div className={styles.scrollAreaPaddingX}>
                          {treeData.map((node) => (
                            <TreeNode
                              key={node.id}
                              node={node}
                              onToggle={toggleNode}
                              onSelect={handleSelectNode}
                              onFavoriteToggle={handleFavoriteToggle}
                              listOfFavorites={listOfFavorites}
                              inventoryName={inventory_name}
                            />
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                    <div
                      className={`${styles.inventoryButtonContainer} ${styles.setFavoriteContainer}`}
                    >
                      {inventory_name && (
                        <button
                          onClick={saveAsFavorite}
                          disabled={loading}
                          className={`${styles.commonButton} ${styles.applyButton} ${styles.setFavoriteButton}`}
                        >
                          {loading ? "Loading..." : "Set Favorite"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={styles.emptyStateContainer}>
                    <Info className={styles.emptyStateIcon} />

                    <div className={styles.inventoryTextCenter}>
                      <h3 className={styles.emptyStateTitle}>
                        No Data to Display
                      </h3>

                      <p className={styles.emptyStateText}>
                        Select columns and apply them to generate the pivot tree
                      </p>
                    </div>

                    {selectedColumns.length > 0 && (
                      <button
                        onClick={applyColumns}
                        className={`${styles.commonButton} ${styles.outlineButton} ${styles.refreshButton}`}
                      >
                        Refresh Data
                      </button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDetail;
